import { useEffect, useMemo, useState } from "react";
import {
  Map,
  Plus,
  Loader2,
  AlertCircle,
  Trash2,
  Upload,
  FileText,
  FileCode,
  Check,
  Tablet,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  ApiError,
  patterns as patternsApi,
  devices as devicesApi,
  type PatternRow,
  type DeviceRow,
} from "@/lib/api";
import { parsePatternCSV } from "@/lib/patternCsvParser";
import { parsePatternPDF } from "@/lib/patternPdfParser";
import { parseDxfZoneLines } from "@/lib/dxfParser";
import type { PatternAnnotation, PatternZoneLine } from "@/types/pattern";

export default function Patterns() {
  const [rows, setRows] = useState<PatternRow[]>([]);
  const [allDevices, setAllDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [p, d] = await Promise.all([patternsApi.list(), devicesApi.list()]);
      setRows(p);
      setAllDevices(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load patterns");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleDelete(id: string) {
    try {
      await patternsApi.remove(id);
      setRows((r) => r.filter((p) => p.id !== id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Delete failed");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Drill Patterns</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload SHOTPlus drill patterns and push them to tablets at a site.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Upload Pattern
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Map className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No patterns uploaded yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {rows.map((p) => {
            const assignedDevices = allDevices.filter((d) =>
              p.device_ids.includes(d.id)
            );
            return (
              <Card key={p.id} className="bg-card border-border">
                <CardContent className="flex items-center justify-between py-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Map className="h-4 w-4 text-primary" />
                      <span className="font-medium text-foreground">{p.pattern_id}</span>
                      <span className="text-xs text-muted-foreground">({p.file_name})</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{p.total_holes} holes</span>
                      <span>Site: {p.site_id}</span>
                      <span>
                        {new Date(p.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {assignedDevices.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <Tablet className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {assignedDevices.map((d) => d.label).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(p.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PatternUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        devices={allDevices}
        onSuccess={() => {
          setUploadOpen(false);
          void refresh();
        }}
      />
    </div>
  );
}

// ─── Upload Dialog ───

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: DeviceRow[];
  onSuccess: () => void;
}

function PatternUploadDialog({ open, onOpenChange, devices, onSuccess }: UploadDialogProps) {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [dxfFile, setDxfFile] = useState<File | null>(null);
  const [siteId, setSiteId] = useState("");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);

  // Filter devices by site
  const siteDevices = useMemo(
    () =>
      devices.filter(
        (d) => !d.revoked && (!siteId || !d.site_id || d.site_id === siteId)
      ),
    [devices, siteId]
  );

  function reset() {
    setCsvFile(null);
    setPdfFile(null);
    setDxfFile(null);
    setSiteId("");
    setSelectedDeviceIds([]);
    setParseErrors([]);
  }

  function toggleDevice(id: string) {
    setSelectedDeviceIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  }

  function selectAllSiteDevices() {
    setSelectedDeviceIds(siteDevices.map((d) => d.id));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!csvFile || !siteId) return;

    setSubmitting(true);
    setParseErrors([]);

    try {
      // Parse CSV
      const csvText = await csvFile.text();
      const { patternId, holes, errors: csvErrors } = parsePatternCSV(csvText);
      if (csvErrors.length > 0) {
        setParseErrors(csvErrors);
        setSubmitting(false);
        return;
      }
      if (holes.length === 0) {
        setParseErrors(["No valid holes found in CSV"]);
        setSubmitting(false);
        return;
      }

      let annotations: PatternAnnotation[] | undefined;
      let zoneLines: PatternZoneLine[] | undefined;

      // Parse PDF if provided
      if (pdfFile) {
        const pdfData = await pdfFile.arrayBuffer();
        const pdfResult = await parsePatternPDF(pdfData, holes);
        if (pdfResult.errors.length > 0) {
          setParseErrors((prev) => [...prev, ...pdfResult.errors]);
        }
        if (pdfResult.annotations.length > 0) annotations = pdfResult.annotations;
        if (pdfResult.zoneLines.length > 0) zoneLines = pdfResult.zoneLines;
      }

      // Parse DXF if provided
      if (dxfFile) {
        const dxfText = await dxfFile.text();
        const minE = Math.min(...holes.map((h) => h.collarEasting)) - 50;
        const maxE = Math.max(...holes.map((h) => h.collarEasting)) + 50;
        const minN = Math.min(...holes.map((h) => h.collarNorthing)) - 50;
        const maxN = Math.max(...holes.map((h) => h.collarNorthing)) + 50;
        const dxfResult = parseDxfZoneLines(dxfText, { bounds: { minE, maxE, minN, maxN } });
        if (dxfResult.errors.length > 0) {
          setParseErrors((prev) => [...prev, ...dxfResult.errors]);
        }
        if (dxfResult.zoneLines.length > 0) {
          zoneLines = [...(zoneLines ?? []), ...dxfResult.zoneLines];
        }
      }

      // Count hole types
      const count = (type: string) => holes.filter((h) => h.holeType === type).length;

      const patternData = {
        holes,
        annotations,
        zoneLines,
        totalHoles: holes.length,
        productionHoles: count("production"),
        batterHoles: count("batter"),
        bufferHoles: count("buffer"),
        tRowHoles: count("t-row"),
      };

      await patternsApi.create({
        pattern_id: patternId,
        site_id: siteId,
        file_name: csvFile.name,
        pattern_data: patternData,
        total_holes: holes.length,
        device_ids: selectedDeviceIds,
      });

      reset();
      onSuccess();
    } catch (err) {
      setParseErrors([err instanceof ApiError ? err.message : "Upload failed"]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Drill Pattern</DialogTitle>
          <DialogDescription>
            Import a SHOTPlus CSV with optional PDF map and DXF ore boundaries, then assign to
            tablets at a site.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Site */}
          <div className="space-y-1.5">
            <Label htmlFor="site">Site *</Label>
            <Input
              id="site"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="e.g. Wodgina Lithium Mine"
            />
          </div>

          {/* File pickers — operator style 3-column cards */}
          <div className="grid grid-cols-3 gap-3">
            {/* CSV */}
            <div className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-lg">
              <FileText className="h-6 w-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground text-center font-medium">
                {csvFile?.name || "SHOTPlus CSV"}
              </p>
              <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">
                Hole positions, depths and diameters
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById("csv-input")?.click()}
              >
                <Upload className="h-3 w-3 mr-1.5" />
                CSV
              </Button>
              <span className="text-[10px] text-destructive/70 font-medium">Required</span>
              <input
                id="csv-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
              />
            </div>

            {/* PDF */}
            <div className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-lg">
              <FileText className="h-6 w-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground text-center font-medium">
                {pdfFile?.name || "Drill Map PDF"}
              </p>
              <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">
                Drilling instructions and ore zones
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById("pdf-input")?.click()}
              >
                <Upload className="h-3 w-3 mr-1.5" />
                PDF
              </Button>
              <span className="text-[10px] text-muted-foreground/50">Optional</span>
              <input
                id="pdf-input"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
              />
            </div>

            {/* DXF */}
            <div className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-lg">
              <FileCode className="h-6 w-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground text-center font-medium">
                {dxfFile?.name || "Ore Body DXF"}
              </p>
              <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">
                Ore zone boundaries from Vulcan
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById("dxf-input")?.click()}
              >
                <Upload className="h-3 w-3 mr-1.5" />
                DXF
              </Button>
              <span className="text-[10px] text-muted-foreground/50">Optional</span>
              <input
                id="dxf-input"
                type="file"
                accept=".dxf"
                className="hidden"
                onChange={(e) => setDxfFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          {/* Device assignment */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Tablet className="h-3.5 w-3.5" />
              Assign to tablets
            </Label>
            {siteDevices.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {siteId
                  ? "No active tablets at this site."
                  : "Enter a site to see available tablets."}
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={selectAllSiteDevices}
                  >
                    Select all ({siteDevices.length})
                  </Button>
                  {selectedDeviceIds.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {selectedDeviceIds.length} selected
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                  {siteDevices.map((d) => {
                    const checked = selectedDeviceIds.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        className={cn(
                          "flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded border",
                          checked
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-accent"
                        )}
                        onClick={() => toggleDevice(d.id)}
                      >
                        <Check
                          className={cn(
                            "h-3.5 w-3.5 shrink-0",
                            checked ? "opacity-100 text-primary" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{d.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Parse errors */}
          {parseErrors.length > 0 && (
            <div className="bg-destructive/10 text-destructive text-xs p-3 rounded space-y-1 max-h-32 overflow-y-auto">
              {parseErrors.map((e, i) => (
                <p key={i}>{e}</p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => { e.preventDefault(); void handleSubmit(e as unknown as React.FormEvent); }}
            disabled={submitting || !csvFile || !siteId}
            className="gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload &amp; Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
